import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import {
  CoreShopSettingsInternationalTaxProfile,
  core_apimessages_TaxIdentification_TaxType as TaxType,
} from '@reverbdotcom/commons/src/gql/graphql';
import { RCAlertBox, RCLoadingBars } from '@reverbdotcom/cadence/components';
import InternationalTaxProfile from './international_tax_profile';
import InternationalTaxProfileForm from './international_tax_profile_form';
import InternationalTaxProfileMainFormSection from './international_tax_profile_main_form_section';
import ErrorDisplay from '../discovery/error_display';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';

type Props = ChildProps<any, CoreShopSettingsInternationalTaxProfile.Query>;

export function InternationalTaxProfileContent({ data }: Props) {
  const businessRegistrationNumber = data?.me?.shop?.businessRegistrationNumber;
  const internationalTaxProfile = data?.me?.shop?.internationalTaxProfile;
  const vatId = (data?.me?.shop?.taxIdentifications || []).find(taxIdentification => (
    taxIdentification.taxType === TaxType.VAT && taxIdentification.countryCode === data?.me?.shop?.address?.countryCode
  ));

  const [isEditing, setIsEditing] = useState(!internationalTaxProfile?.name);
  const [isSuccess, setIsSuccess] = useState(false);

  const switchToEditMode = useCallback(() => {
    setIsSuccess(false);
    setIsEditing(true);
  }, [setIsEditing, setIsSuccess]);

  useEffect(() => {
    if (!data?.loading && internationalTaxProfile?.name) {
      setIsEditing(false);
    }
  }, [data?.loading]);

  if (data?.loading) {
    return (
      <InternationalTaxProfileMainFormSection>
        <div className="d-flex fx-align-center fx-justify-center padding-8">
          <RCLoadingBars />
        </div>
      </InternationalTaxProfileMainFormSection>
    );
  }

  if (data?.error) {
    return (
      <InternationalTaxProfileMainFormSection>
        <ErrorDisplay />
      </InternationalTaxProfileMainFormSection>
    );
  }


  return (
    isEditing ? (
      <InternationalTaxProfileForm
        businessRegistrationNumber={businessRegistrationNumber}
        countries={data?.countries?.countries}
        internationalTaxProfile={internationalTaxProfile}
        isShopInEU={data?.me?.shop?.isShopInEu}
        setIsEditing={setIsEditing}
        setIsSuccess={setIsSuccess}
        shopType={data?.me?.shop?.shopType}
        vatId={vatId}
      />
    ) : (
      <>
        {isSuccess && (
          <div className="scaling-mt-3">
            <RCAlertBox
              type="success"
            >
              <Translate text="discovery.internationalTaxProfile.form.success" />
            </RCAlertBox>
          </div>
        )}
        <InternationalTaxProfile
          businessRegistrationNumber={businessRegistrationNumber}
          internationalTaxProfile={internationalTaxProfile}
          isShopInEU={data?.me?.shop?.isShopInEu}
          switchToEditMode={switchToEditMode}
          vatId={vatId}
        />
      </>
    )
  );
}

const withQuery = withGraphql<any, CoreShopSettingsInternationalTaxProfile.Query>(
  gql`
    query Core_ShopSettings_InternationalTaxProfile {
      me {
        uuid
        shop {
          _id
          internationalTaxProfile {
            id
            tinMask
            tinType
            name
            dateOfBirth
            address {
              _id
              name
              streetAddress
              extendedAddress
              locality
              region
              postalCode
              countryCode
              country {
                _id
                name
              }
            }
          }
          businessRegistrationNumber {
            id
            registrationNumber
            countryCode
          }
          taxIdentifications {
            id
            taxId
            taxType
            countryCode
          }
          address {
            _id
            countryCode
          }
          shopType
          isShopInEu
        }
      }
      countries {
        countries {
          _id
          countryCode
          name
          subregionRequired
          subregions {
            _id
            code
            name
          }
        }
      }
    }
  `,
);

export default withQuery(InternationalTaxProfileContent);
